import { c as _c } from "react/compiler-runtime";
import { useState, type ReactElement } from 'react';
import type { ChartDataRow } from '../../../types';
import Dropdown, { DropdownItem, DropdownList } from '@precis-digital/kurama/Dropdown';
import PinIcon from '@precis-digital/kurama/icons/PinIcon';
import Tooltip from '@precis-digital/kurama/Tooltip';
import { getPinPositionForColumnId } from '../utils';
import { defaultTFunction } from '../../../utils/generics';
import type { ChartCardTableHeaderContextResolved } from '../tanstack';
const getDropDownItems = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(props: ChartCardTableHeaderContextResolved<DataKey, DataRow>, handleClose: () => void): ReactElement[] => {
  const {
    meta: contextMeta,
    ...context
  } = props;
  const {
    columnPinning,
    t = defaultTFunction
  } = contextMeta.chartCard;
  const {
    id: columnId,
    pin
  } = context.column;
  const pinned = getPinPositionForColumnId(columnPinning, columnId);
  const noPinOnClick = (): void => {
    pin(false);
    handleClose();
  };
  const pinLeftOnClick = (): void => {
    pin('left');
    handleClose();
  };
  const pinRightOnClick = (): void => {
    pin('right');
    handleClose();
  };
  const dropDownItems: ReactElement[] = [<DropdownItem key="no_pin" disabled={pinned === false} onClick={noPinOnClick} leftIcon={<PinIcon />}>
      {t('noPin')}
    </DropdownItem>, <DropdownItem key="pin_left" disabled={pinned === 'left'} onClick={pinLeftOnClick} leftIcon={<PinIcon />}>
      {t('pinLeft')}
    </DropdownItem>, <DropdownItem key="pin_right" disabled={pinned === 'right'} onClick={pinRightOnClick} leftIcon={<PinIcon />}>
      {t('pinRight')}
    </DropdownItem>];
  return dropDownItems;
};
interface TableHeadCellPinningMenuProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> extends ChartCardTableHeaderContextResolved<DataKey, DataRow> {
  handleClose: () => void;
}
const TableHeadCellPinningMenu = t0 => {
  const $ = _c(14);
  const {
    handleClose: handleCloseParent,
    ...props
  } = t0;
  const {
    meta: contextMeta,
    ...context
  } = props;
  const {
    t: t1
  } = contextMeta.chartCard;
  const t = t1 === undefined ? defaultTFunction : t1;
  const {
    columnDef: t2
  } = context.column;
  const {
    meta: columnMeta
  } = t2;
  const fixedPin = columnMeta?.chartCard?.fixedPin ?? false;
  const isFixedPin = typeof fixedPin === "string";
  const [dropdownAnchorEl, setDropdownAnchorEl] = useState(null);
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = event => {
      event.stopPropagation();
      setDropdownAnchorEl(event.currentTarget);
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const handleClick = t3;
  let t4;
  if ($[1] !== handleCloseParent) {
    t4 = () => {
      setDropdownAnchorEl(null);
      handleCloseParent();
    };
    $[1] = handleCloseParent;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const handleClose = t4;
  const dropDownItems = getDropDownItems(props, handleClose);
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <PinIcon />;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let dropdownItemElement = <DropdownItem key="pin_menu" disabled={isFixedPin} onClick={handleClick} leftIcon={t5}>{t("pinColumn")}</DropdownItem>;
  if (isFixedPin) {
    const title = fixedPin === "no" ? t("noPinColumn") : t("noUnpinColumn");
    dropdownItemElement = <Tooltip key="pin_menu_tooltip" kind="singleline" title={title}>{dropdownItemElement}</Tooltip>;
  }
  const t6 = dropdownAnchorEl != null;
  let t7;
  if ($[4] !== dropDownItems) {
    t7 = <DropdownList minWidth="300px">{dropDownItems}</DropdownList>;
    $[4] = dropDownItems;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== dropdownAnchorEl || $[7] !== handleClose || $[8] !== t6 || $[9] !== t7) {
    t8 = <Dropdown key="pin_menu_dropdown" anchorEl={dropdownAnchorEl} open={t6} onClose={handleClose}>{t7}</Dropdown>;
    $[6] = dropdownAnchorEl;
    $[7] = handleClose;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== dropdownItemElement || $[12] !== t8) {
    t9 = <>{dropdownItemElement}{t8}</>;
    $[11] = dropdownItemElement;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  return t9;
};
export default TableHeadCellPinningMenu;